import { useCallback, useMemo, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { SystemConfig } from "../Types/SystemConfig";
import { SYSTEM_CONFIG_REGISTRY } from "../Config/systemConfigRegistry";
import { useSystemConfigQuery, systemConfigQueryKeys } from "../Services";
import { tokenManager } from "../Utils";
import {
  SYSTEM_CONFIG_CACHE_KEY,
  SYSTEM_CONFIG_CACHE_TS_KEY,
  SYSTEM_CONFIG_CACHE_DURATION,
} from "../Constant";

export interface UseSystemConfigReturn {
  config: SystemConfig | null;
  isLoading: boolean;
  error: Error | null;
  get: (key: string) => string | number | boolean | undefined;
  refresh: () => Promise<void>;
  clearCache: () => void;

  appName: string;
  appLogo: string;
  storeName: string;
  address: string;
  phone: string;
  email: string;
  whatsapp: string;
  currency: string;
  taxRate: number;
  maxDiscount: number;
  receiptFooter: string;
  language: string;
  timezone: string;

  isService: boolean;
  isMobileShop: boolean;
  isRestaurant: boolean;
  isOnlineStore: boolean;
  isWhatsAppEnabled: boolean;
  isCourierEnabled: boolean;
  isSmsGatewayEnable: boolean;
  smsGatewaySenderId: string;
  isPaymentUpcoming: boolean;
  isPaymentWarning: boolean;
  isTerminated: boolean;
  isEnable: boolean;
  maintenanceMode: boolean;
}

export const useSystemConfig = (): UseSystemConfigReturn => {
  const queryClient = useQueryClient();

  const query = useSystemConfigQuery({
    enabled: !!tokenManager.getToken(),
    initialData: () => {
      try {
        const cached = localStorage.getItem(SYSTEM_CONFIG_CACHE_KEY);
        const ts = localStorage.getItem(SYSTEM_CONFIG_CACHE_TS_KEY);
        if (
          cached &&
          ts &&
          Date.now() - parseInt(ts, 10) < SYSTEM_CONFIG_CACHE_DURATION
        ) {
          return JSON.parse(cached) as SystemConfig;
        }
      } catch {}
      return undefined;
    },
  });

  const config = query.data || null;

  useEffect(() => {
    if (config) {
      localStorage.setItem(SYSTEM_CONFIG_CACHE_KEY, JSON.stringify(config));
      localStorage.setItem(SYSTEM_CONFIG_CACHE_TS_KEY, Date.now().toString());
    }
  }, [config]);

  const refresh = useCallback(async () => {
    await query.refetch();
  }, [query]);

  const clearCache = useCallback(() => {
    localStorage.removeItem(SYSTEM_CONFIG_CACHE_KEY);
    localStorage.removeItem(SYSTEM_CONFIG_CACHE_TS_KEY);
    queryClient.invalidateQueries({
      queryKey: systemConfigQueryKeys.systemConfig(),
    });
  }, [queryClient]);

  const parsedConfig = useMemo(() => {
    if (!config) return null;
    const result: Record<string, any> = {};
    for (const field of SYSTEM_CONFIG_REGISTRY) {
      result[field.key] = field.parse(config[field.key]);
    }
    return result;
  }, [config]);

  const get = useCallback(
    (key: string) => {
      if (parsedConfig && parsedConfig[key] !== undefined) {
        return parsedConfig[key];
      }
      if (!config) return undefined;
      const field = SYSTEM_CONFIG_REGISTRY.find((f: any) => f.key === key);
      if (!field) return config?.[key];
      return field.parse(config?.[key]);
    },
    [config, parsedConfig],
  );

  return {
    config,
    isLoading: query.isLoading,
    error: query.error as Error | null,
    get,
    refresh,
    clearCache,

    appName: parsedConfig?.app_name || "POS System",
    appLogo: parsedConfig?.app_logo || "applogo.jpg",
    storeName: parsedConfig?.store_name || "POS System",
    address: parsedConfig?.address || "",
    phone: parsedConfig?.phone || "",
    email: parsedConfig?.email || "",
    whatsapp: parsedConfig?.whatsapp || "",
    currency: parsedConfig?.currency || "LKR",
    taxRate: parsedConfig?.tax_rate || 0,
    maxDiscount: parsedConfig?.max_discount || 100,
    receiptFooter: parsedConfig?.receipt_footer || "",
    language: parsedConfig?.language || "en",
    timezone: parsedConfig?.timezone || "Asia/Colombo",

    isService: !!parsedConfig?.is_service,
    isMobileShop: !!parsedConfig?.is_mobileshop,
    isRestaurant: !!parsedConfig?.is_restaurant,
    isOnlineStore: !!parsedConfig?.is_onlinestore_enabled,
    isWhatsAppEnabled: !!parsedConfig?.is_whatsapp_enabled,
    isCourierEnabled: !!parsedConfig?.is_courier_enabled,
    isSmsGatewayEnable: !!parsedConfig?.is_sms_gateway_enable,
    smsGatewaySenderId: parsedConfig?.sms_gateway_senderid || "",
    isPaymentUpcoming: !!parsedConfig?.is_payment_upcoming,
    isPaymentWarning: !!parsedConfig?.is_payment_warning,
    isTerminated: !!parsedConfig?.is_terminated,
    isEnable: !!parsedConfig?.is_enable,
    maintenanceMode: !!parsedConfig?.maintenance_mode,
  };
};
